import { Controller } from 'stimulus';
import { createDatepicker, teamDateFormat, Datepicker } from '@/js/modules/datepicker';

export default class extends Controller {
  static targets = ['select', 'customDate', 'picker'];
  static values = { defaultDate: String };

  #datepicker = null;
  #committedIndex = 0;

  connect() {
    this.#committedIndex = this.selectTarget.selectedIndex;
  }

  disconnect() {
    this.#closePicker();
  }

  selectionChanged() {
    if (this.#selectedOption?.dataset.customTrigger !== undefined) {
      this.#openPicker();
    } else {
      this.#closePicker();
      this.#commit('');
    }
  }

  #openPicker() {
    if (this.#datepicker) {
      return;
    }
    this.pickerTarget.dataset.date = this.defaultDateValue;
    this.pickerTarget.classList.remove('hidden');
    this.#datepicker = createDatepicker(this.pickerTarget);
    this.pickerTarget.addEventListener('changeDate', this.#dateChosen);
    document.addEventListener('pointerdown', this.#abortOnOutsideClick, true);
    document.addEventListener('keydown', this.#abortOnEscape);
  }

  #dateChosen = (event) => {
    const date = event.detail.date;
    this.#upsertDateOption(Datepicker.formatDate(date, teamDateFormat()));
    this.#closePicker();
    this.#commit(Datepicker.formatDate(date, 'yyyy-mm-dd'));
  };

  #upsertDateOption(label) {
    let option = this.#dateOption;
    if (!option) {
      option = new Option(label, '');
      option.dataset.customDate = '';
      this.#triggerOption.before(option);
    }
    option.text = label;
    option.selected = true;
  }

  #commit(isoDate) {
    if (this.#selectedOption?.dataset.customDate === undefined) {
      this.#dateOption?.remove();
    }
    this.#committedIndex = this.selectTarget.selectedIndex;
    this.customDateTarget.value = isoDate;
    this.customDateTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #abortOnOutsideClick = (event) => {
    if (!this.element.contains(event.target)) {
      this.#abort();
    }
  };

  #abortOnEscape = (event) => {
    if (event.key === 'Escape') {
      this.#abort();
    }
  };

  #abort() {
    this.selectTarget.selectedIndex = this.#committedIndex;
    this.#closePicker();
  }

  #closePicker() {
    if (!this.#datepicker) {
      return;
    }
    this.pickerTarget.removeEventListener('changeDate', this.#dateChosen);
    document.removeEventListener('pointerdown', this.#abortOnOutsideClick, true);
    document.removeEventListener('keydown', this.#abortOnEscape);
    this.#datepicker.destroy();
    this.#datepicker = null;
    this.pickerTarget.classList.add('hidden');
  }

  get #selectedOption() {
    return this.selectTarget.selectedOptions[0];
  }

  get #dateOption() {
    return this.selectTarget.querySelector('option[data-custom-date]');
  }

  get #triggerOption() {
    return this.selectTarget.querySelector('option[data-custom-trigger]');
  }
}
